/**
 * Template field editability policy (Phase 2a).
 *
 * A template (pronumber starts with "T") reuses the Order Info / Legs / Addl Info /
 * Tracing / Invoicing surfaces but with per-field editability driven by this policy
 * instead of the blanket read-only lock used in Phase 1. Three states (see
 * docs/templates-port/FIELD_AUDIT.md — the resolved spec is the source of truth):
 *
 *   1 = always locked        (never editable on a template)
 *   2 = locked when approved (editable only while the template is unapproved)
 *   3 = always open          (editable regardless of approval)
 *
 * Default is 1: any field NOT listed below is always locked. Only the 2s and 3s
 * are enumerated. Keys are the OrderDetails / leg data keys used by the field
 * components; lookup-backed fields (consignee/broker/billto/carrier/movetype)
 * resolve their concrete edit-key (code/recno) in the component — the policy only
 * decides *whether* the field is editable.
 *
 * IMPORTANT: this policy applies to TEMPLATES only. For a normal order, callers keep
 * their existing editability logic unchanged — never route an order through here.
 */

export type TemplateFieldState = 2 | 3;

/** Fields that are editable on a template. Everything else defaults to 1 (locked). */
export const TEMPLATE_FIELD_STATE: Record<string, TemplateFieldState> = {
  // ---- Order Info · People & Equipment ----
  csr: 3,
  whseCsr: 3,
  dispatcher: 3,
  carrier: 3, // conditional (only when dispatcher is non-GWI) — enforced in the component
  chassisProvider: 3,
  chassisType: 3,
  scac: 3,
  size: 2,
  equipType: 2,
  isGwiEquip: 2,

  // ---- Order Info · Move Flags ----
  scale: 3, // forced 'Y' + locked when otr='Y' (cross-field rule, in the component)
  otr: 3,
  isTank: 3,
  hot: 3,
  isDrop: 3, // mirrors delivery-leg legDrop (cross-field rule)
  hazmat: 2,

  // ---- Order Info · Locations & Routing ----
  pieceCount: 3,
  commodity: 3,
  portOfOrigin: 3,
  portOfDischarge: 3,
  consigneeName: 3, // location lookup (edits consigneeCode); prompts consignee-copy on delivery change
  brokerName: 3,
  brokerContactName: 3,

  // ---- Order Info · Comments ----
  comments: 3,
  dropReleaseComment: 3,
  bobtailApproved: 3, // Y/N flag beside Drop Rel; a blueprint value, so open like the memo

  // ---- Legs (per leg) ----
  legtype: 2,
  origcode: 2,
  origmemo: 3,
  legAction2: 2, // delivery leg type — unlock is template+unapproved only (see FIELD_AUDIT rule #3)
  destcode: 2, // delivery location — same gating
  legmemo: 3,
  legDrop: 3, // mirrors order isdrop
  // ONE key for the destination appt time RANGE (destappttime1 + destappttime2) —
  // the range cell is gated as a unit, so the policy names the pair, not each
  // column. State 3: a delivery window ("08:00-16:00") is blueprint data, unlike
  // the appt DATE beside it, which stays State 1 (a date can't ride a template).
  destApptTime: 3,

  // ---- Additional Info · Customer & Yard ----
  customerName: 3,
  dcNumber: 3,
  dept: 3,
  allowBrokerage: 3,
  streetTurnHold: 3,

  // ---- Additional Info · Customs & Warehouse ----
  hazmatInfo: 3, // hazmat lines (sub-entity)

  // ---- Additional Info · Audit & Reuse ----
  masterPro: 3,
  moveType: 2, // key-shape field (re-quote trigger)
  templateType: 2, // key-shape field (re-quote trigger)
  gwiMove: 3,
  sendEmailNotifications: 3,
  addlInfoDeleted: 3,

  // ---- Tracing ----
  traceMode: 3,

  // ---- Invoicing ----
  rateComments: 3,
  billToName: 2, // key-shape field (re-quote trigger); billto lookup
  billToContactName: 3, // contactinfo lookup by billto
  freightTerms: 3,
  requireEdi: 3,
  requirePod: 3,
  requireBol: 3,
  requireMrpl: 3,
  requireInInterchange: 3,
  requireOutInterchange: 3,
  requireChassisInEir: 3,
  requireChassisOutEir: 3,
  requireRevisedDo: 3,
  pendingApproval: 3,
  estInvoiceEmail: 3,
  billFromSystem: 2,
  doNotBillReason: 2,
  quoteNumber: 2,
  revisionNumber: 2,
  invoiceComments: 3,
  marginLimitLocal: 3,

  // Invoice line-item columns (grid). Editable, State 2 — EXCEPT `total` (always
  // calculated → 1, omitted here) and `glCode` (stays editable after approval → 3).
  item: 2,
  qty: 2,
  rate: 2,
  rateType: 2,
  description: 2,
  lineNumber: 2,
  glCode: 3,
};

/** Is this pronumber a template ("T"-prefixed)? */
export function isTemplatePronumber(pronumber: string | number | null | undefined): boolean {
  return typeof pronumber === 'string' && pronumber.trim().toUpperCase().startsWith('T');
}

/**
 * Is a template approved? The approval switch is the order-level `approvedby`
 * (v1 `order_approvedby`), surfaced in the details payload as `invoiceApprovedBy`
 * (= `o.approvedby`). Non-empty → approved → State-2 fields lock.
 */
export function isTemplateApproved(
  details: { invoiceApprovedBy?: string | null } | null | undefined,
): boolean {
  return !!(details?.invoiceApprovedBy && String(details.invoiceApprovedBy).trim() !== '');
}

/**
 * Whether a given field is editable on a TEMPLATE, per the policy + approval state.
 * Only call this for templates; orders keep their own editability logic.
 */
export function templateFieldEditable(fieldKey: string, opts: { isApproved: boolean }): boolean {
  const state = TEMPLATE_FIELD_STATE[fieldKey];
  if (state === 3) return true;
  if (state === 2) return !opts.isApproved;
  return false; // undefined (default 1) or any other → always locked
}
